import * as styles from "./ContactPreview.css";

import { clsx } from "clsx";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { ICON_SIZES } from "@frontend/components/atoms/Icon/constants";
import {
  PopoverTrigger,
  type PopoverTriggerProps,
} from "@frontend/components/atoms/Popover";
import { Spinner } from "@frontend/components/atoms/Spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useLingui } from "@lingui/react";

import { ContactPreviewLabel, ContactPreviewLoadingLabel } from "../../i18n";
import {
  contactPreviewHandle,
  useContactPreviewTrigger,
} from "./contactPreviewGroup";

export type ContactPreviewPopoverTriggerProps = Omit<
  PopoverTriggerProps<number>,
  "handle" | "id" | "payload"
> & {
  readonly label?: string;
  readonly avatarUrl?: string;
  readonly highlighted?: boolean;
};

export function ContactPreviewPopoverTrigger({
  render,
  label,
  avatarUrl,
  highlighted,
  onClick,
  onPointerEnter,
  onPointerLeave,
  ...props
}: ContactPreviewPopoverTriggerProps) {
  const { _ } = useLingui();
  const trigger = useContactPreviewTrigger();

  const sharedProps: PopoverTriggerProps<number> = {
    ...props,
    handle: contactPreviewHandle,
    id: trigger.triggerId,
    payload: trigger.contactId,
    onClick: (event) => {
      onClick?.(event);
      trigger.activate();
    },
    onPointerEnter: (event) => {
      onPointerEnter?.(event);
      trigger.onPointerEnter(event.pointerType);
    },
    onPointerLeave: (event) => {
      onPointerLeave?.(event);
      trigger.onPointerLeave();
    },
  };

  if (render !== undefined) {
    return <PopoverTrigger {...sharedProps} render={render} />;
  }

  const title =
    label !== undefined && label !== "" ? label : _(ContactPreviewLabel);

  const name = trigger.loading ? _(ContactPreviewLoadingLabel) : title;

  return (
    <Tooltip disabled={trigger.cardOpen}>
      <TooltipTrigger
        render={
          <PopoverTrigger
            {...sharedProps}
            render={(triggerProps) =>
              avatarUrl === undefined ? (
                <Button
                  {...triggerProps}
                  variant="ghost"
                  size="sm"
                  aria-label={name}
                  disableRipple
                >
                  {trigger.loading ? (
                    <Spinner size={ICON_SIZES.sm} />
                  ) : (
                    <Icon name="Info" size="sm" />
                  )}
                </Button>
              ) : (
                <button
                  type="button"
                  {...triggerProps}
                  className={styles.avatarTrigger}
                  aria-label={name}
                >
                  <Avatar
                    size="sm"
                    url={trigger.loading ? undefined : avatarUrl}
                    fallback={
                      trigger.loading ? (
                        <Spinner size={ICON_SIZES.md} />
                      ) : undefined
                    }
                    className={clsx(highlighted && styles.highlightedAvatar)}
                  />
                </button>
              )
            }
          />
        }
      />

      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}
